import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Download,
  Share2,
  Mail,
  Printer,
  CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import QRCodeDisplay from '@/components/QRCodeDisplay';
import { api } from '@/services/api';

interface EnhancedReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
}

const EnhancedReceiptModal: React.FC<EnhancedReceiptModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
}) => {
  const [qrCode, setQrCode] = useState<string>('');
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const loadEnhancedReceipt = async () => {
    if (!invoiceId) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`/api/receipts/${invoiceId}/enhanced?format=json`);
      setQrCode(response.data.qrCode);
      setShareUrl(response.data.shareUrl);
    } catch (error) {
      toast.error('Failed to load enhanced receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!invoiceId) return;
    
    try {
      const response = await api.get(`/api/receipts/${invoiceId}/enhanced?format=pdf`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const shareReceipt = async () => {
    if (!invoiceId) return;
    
    try {
      const response = await api.post(`/api/receipts/${invoiceId}/share`);
      const { url } = response.data;
      
      if (navigator.share) {
        await navigator.share({
          title: `Receipt ${invoiceNumber}`,
          text: 'View your receipt',
          url: url,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard');
      }
    } catch (error) {
      toast.error('Failed to share receipt');
    }
  };

  const emailReceipt = async () => {
    if (!invoiceId || !emailAddress) return;
    
    try {
      const response = await api.post(`/receipts/${invoiceId}/share`, {
        emailAddress,
      });
      
      if (response.data.success) {
        toast.success('Receipt emailed successfully');
        setShowEmailForm(false);
        setEmailAddress('');
      } else {
        toast.error('Failed to email receipt');
      }
    } catch (error) {
      toast.error('Failed to email receipt');
    }
  };

  const printReceipt = () => {
    window.print();
  };

  React.useEffect(() => {
    if (isOpen && invoiceId) {
      loadEnhancedReceipt();
    }
  }, [isOpen, invoiceId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Enhanced Receipt - {invoiceNumber}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading enhanced receipt...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* QR Code Section */}
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Verification QR Code</h3>
                  <QRCodeDisplay 
                    qrCodeDataUrl={qrCode} 
                    size={200}
                    title="Scan to verify receipt"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Scan this QR code to verify the authenticity of this receipt
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={downloadPDF}
                    className="flex items-center justify-center"
                    variant="primary"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  <Button
                    onClick={shareReceipt}
                    className="flex items-center justify-center"
                    variant="secondary"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  
                  <Button
                    onClick={() => setShowEmailForm(!showEmailForm)}
                    className="flex items-center justify-center"
                    variant="secondary"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  
                  <Button
                    onClick={printReceipt}
                    className="flex items-center justify-center"
                    variant="secondary"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>

                {/* Email Form */}
                {showEmailForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 mb-3">Email Receipt</h4>
                    <div className="flex space-x-2">
                      <input
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <Button onClick={emailReceipt} size="sm">
                        Send
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowEmailForm(false);
                          setEmailAddress('');
                        }} 
                        variant="secondary" 
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Share URL */}
                {shareUrl && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Share Link</h4>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={shareUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                      />
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(shareUrl);
                          toast.success('Link copied to clipboard');
                        }}
                        size="sm"
                        variant="secondary"
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      This link will expire in 24 hours for security reasons
                    </p>
                  </div>
                )}

                {/* Security Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">Security Features</h4>
                      <ul className="text-sm text-blue-800 mt-1 space-y-1">
                        <li>• QR code contains encrypted receipt data</li>
                        <li>• Share links expire after 24 hours</li>
                        <li>• All receipts are logged in the audit trail</li>
                        <li>• MRA compliance verification included</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EnhancedReceiptModal;
